import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { joinWaitlist } from '../lib/auth';
import { LogoMark, LogoFull } from '../components/Logo';
import heroImage from '../assets/hero-corner.jpg';
import ScoutingCard from './ScoutingCard';
import ExploreTeams from './ExploreTeams';
import HeroBadges from './HeroBadges';
import './Landing.css';

function WaitlistForm({ source }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | busy | done | error
  const [message, setMessage] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setState('busy');
    const res = await joinWaitlist(email, source);
    if (res.ok) {
      setState('done');
      setMessage(res.already
        ? 'We already had your email. Nothing more to do.'
        : "We'll be in touch when the first save opens.");
    } else {
      setState('error');
      setMessage(res.error);
    }
  }

  if (state === 'done') {
    return (
      <div className="wl-success">
        <span className="wl-tick" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="11" height="11">
            <path d="M2.5 8.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor"
              strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div>
          <div className="wl-success-title">You{'\u2019'}re on the list.</div>
          <div className="wl-success-sub">{message}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <form className="wl-form" onSubmit={handleSubmit}>
        <input
          className="wl-input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@email.com"
          aria-label="Email address"
          required
        />
        <button className="btn btn-primary" type="submit" disabled={state === 'busy'}>
          {state === 'busy' ? 'Joining\u2026' : 'Join waitlist'}
        </button>
      </form>
      {state === 'error' && (
        <div style={{ color: '#FF4D6A', fontSize: 13, marginBottom: 12 }}>{message}</div>
      )}
      <p className="wl-note">
        No spam, just launch news. By joining you agree to our{' '}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </>
  );
}

export default function Landing() {
  const navRef = useRef(null);
  const [phase, setPhase] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'done' : 'intro'
  );

  // Full-screen logo splash on every load, then crossfade to the page.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t1 = setTimeout(() => setPhase('reveal'), 1500);
    const t2 = setTimeout(() => setPhase('done'), 2150);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    // Sticky nav border
    const onScroll = () => {
      if (navRef.current) {
        navRef.current.classList.toggle('stuck', window.scrollY > 12);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Reveal on scroll
    const els = document.querySelectorAll('.lp .rev');
    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      els.forEach(el => io.observe(el));
    } else {
      els.forEach(el => el.classList.add('in'));
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (io) io.disconnect();
    };
  }, []);

  return (
    <div className="lp">


      {phase !== 'done' && (
        <div className={`intro${phase === 'reveal' ? ' intro-leaving' : ''}`} aria-hidden="true">
          <div className="intro-logo"><LogoFull height={54} /></div>
        </div>
      )}
      <div className={`lp-content${phase === 'intro' ? '' : ' in'}`}>

      {/* ═══ NAV ═══ */}
      <nav className="topnav" ref={navRef}>
        <div className="topnav-in">
          <a className="logo-lock" href="#top" aria-label="Groundwork">
            <LogoFull height={24} />
          </a>
          <div className="topnav-links">
            <a className="topnav-link" href="#role">The role</a>
            <a className="topnav-link" href="#world">The world</a>
            <a className="topnav-link" href="#teams">The teams</a>
            <a className="topnav-link" href="#progress">Progress</a>
            <Link className="topnav-link topnav-login" to="/login">Log in</Link>
            <a className="btn btn-primary btn-sm" href="#join">Join waitlist</a>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <header className="hero" id="top">
        <div className="hero-bg" role="presentation" style={{ backgroundImage: `url(${heroImage})` }}></div>
        <div className="hero-veil" role="presentation"></div>
        <div className="wrap hero-badges-wrap"><HeroBadges /></div>
        <div className="wrap hero-in">
          <div>
            <div className="tagline"><span className="dot"></span>In development · Waitlist open</div>
            <h1>Do the <em>groundwork</em>,<br />create a <em>legacy</em>.</h1>
            <p className="hero-sub">
              Groundwork is a football management game where you're the <b>Director of Football</b>.
              You find the players, do the deals and set the direction, then build a club that climbs.
            </p>

            <div className="waitlist">
              <WaitlistForm source="hero" />
            </div>
          </div>

          {/* Scouting mock — live, interactive (typewriter + magnifier) */}
          <ScoutingCard />
        </div>
      </header>

      {/* ═══ NUMBERS ═══ */}
      <div className="numbers">
        <div className="wrap numbers-grid">
          <div className="num rev"><div className="num-v">48</div><div className="num-l">Clubs</div></div>
          <div className="num rev"><div className="num-v">7,400</div><div className="num-l">Players</div></div>
          <div className="num rev"><div className="num-v">27</div><div className="num-l">Scouting regions</div></div>
          <div className="num rev"><div className="num-v">4</div><div className="num-l">Divisions</div></div>
        </div>
      </div>

      {/* ═══ THE ROLE ═══ */}
      <section id="role">
        <div className="wrap">
          <div className="rev">
            <div className="sec-label">The role</div>
            <h2>Everything except the eleven.</h2>
            <p className="sec-intro">
              Most management games hand you the team sheet. Groundwork hands you the club.
              You write the plan, find the players and manage the people, then live with
              what your manager does on a Saturday.
            </p>
          </div>

          <div className="pillars">
            <div className="pillar rev">
              <div className="pillar-ic" style={{color: 'var(--lime)'}}>◈</div>
              <h3>Set the direction</h3>
              <p>Write a club blueprint: playing style, recruitment policy, how much you trust youth.
                 Your manager reads it. Whether he follows it is another matter.</p>
            </div>
            <div className="pillar c-cyan rev">
              <div className="pillar-ic" style={{color: 'var(--cyan)'}}>⬡</div>
              <h3>Find them first</h3>
              <p>Send scouts across fifteen British regions and a dozen countries. Reports come back
                 incomplete. The further you look, the less you can trust what you see.</p>
            </div>
            <div className="pillar c-violet rev">
              <div className="pillar-ic" style={{color: 'var(--violet)'}}>◉</div>
              <h3>Handle the manager</h3>
              <p>He has his own ideas about your signings. Back him, push him, or take it to the board
                 and ask for a change. That conversation has consequences.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SCOUTING ═══ */}
      <section id="world" style={{background: 'var(--bg-surface)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)'}}>
        <div className="wrap">
          <div className="rev">
            <div className="sec-label">Scouting</div>
            <h2>The fog is the game.</h2>
            <p className="sec-intro">
              You never see a player's true ability. You see what your scouts report, and they're
              working with limited time, in places where your network is thin.
            </p>
          </div>

          <div className="feat rev">
            <div>
              <h3>Three tiers of knowing</h3>
              <p>An unscouted player is a surname, a rough age and a position group. Basic scouting
                gives you a name and a <b>ten-point ability range</b>. A full report opens the attributes,
                but the numbers still carry error.</p>
              <p>Scout quality matters. So does distance. A report from Leeds is close to the truth.
                A report from São Paulo is an educated guess.</p>
            </div>
            <div className="feat-vis">
              <div className="panel">
                <div className="panel-head">
                  <span className="panel-title">Scout report</span>
                  <span className="badge b-violet" style={{marginLeft: 'auto'}}>Distant · ±15</span>
                </div>
                <div className="panel-body">
                  <div className="attr"><span className="attr-n">Finishing</span><div className="attr-t"><div className="attr-f" style={{width: '84%', background: 'var(--lime)'}}></div></div><span className="attr-v" style={{color: 'var(--lime)'}}>84~</span></div>
                  <div className="attr"><span className="attr-n">Dribbling</span><div className="attr-t"><div className="attr-f" style={{width: '79%', background: 'var(--lime)'}}></div></div><span className="attr-v" style={{color: 'var(--lime)'}}>79~</span></div>
                  <div className="attr"><span className="attr-n">Composure</span><div className="attr-t"><div className="attr-f" style={{width: '62%', background: 'var(--green)'}}></div></div><span className="attr-v" style={{color: 'var(--green)'}}>62~</span></div>
                  <div className="attr"><span className="attr-n">Strength</span><div className="attr-t"><div className="attr-f" style={{width: '44%', background: 'var(--amber)'}}></div></div><span className="attr-v" style={{color: 'var(--amber)'}}>44~</span></div>
                  <div className="attr"><span className="attr-n">Heading</span><div className="attr-t"><div className="attr-f" style={{width: '31%', background: 'var(--red)'}}></div></div><span className="attr-v" style={{color: 'var(--red)'}}>31~</span></div>
                  <p style={{fontSize: '12.5px', color: 'var(--tx-muted)', marginTop: 'var(--s3)', fontStyle: 'italic'}}>
                    "Quick feet, finishes well. Physically raw. He'd struggle in a British winter."
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="feat rev">
            <div>
              <h3>Cheap at home. Expensive abroad.</h3>
              <p>Scouting South London costs <b>£2,000 and takes a fortnight</b>. Scouting Brazil costs
                <b>£11,000 and takes eight weeks</b>, and the report comes back hazier.</p>
              <p>Foreign players aren't better. The ceilings are the same everywhere. What you're
                buying abroad is variance, not quality. The best player you ever sign might be
                from twenty minutes down the road.</p>
            </div>
            <div className="feat-vis">
              <div className="panel">
                <div className="panel-head"><span className="panel-title">Regions</span></div>
                <div className="panel-body">
                  <div className="regions">
                    <div className="region"><div className="region-n">🏴 South London</div><div className="region-t" style={{color: 'var(--lime)'}}>£2.0k · 2w</div></div>
                    <div className="region"><div className="region-n">🏴 Yorkshire</div><div className="region-t" style={{color: 'var(--lime)'}}>£2.0k · 2w</div></div>
                    <div className="region"><div className="region-n">🇮🇪 Ireland</div><div className="region-t" style={{color: 'var(--green)'}}>£3.6k · 3w</div></div>
                    <div className="region"><div className="region-n">🇵🇹 Portugal</div><div className="region-t" style={{color: 'var(--cyan)'}}>£6.0k · 5w</div></div>
                    <div className="region"><div className="region-n">🇩🇪 Germany</div><div className="region-t" style={{color: 'var(--cyan)'}}>£6.0k · 5w</div></div>
                    <div className="region"><div className="region-n">🇧🇷 Brazil</div><div className="region-t" style={{color: 'var(--violet)'}}>£11k · 8w</div></div>
                    <div className="region"><div className="region-n">🇬🇭 Ghana</div><div className="region-t" style={{color: 'var(--violet)'}}>£11k · 8w</div></div>
                    <div className="region"><div className="region-n">🇯🇵 Japan</div><div className="region-t" style={{color: 'var(--violet)'}}>£11k · 8w</div></div>
                  </div>
                  <div style={{display: 'flex', gap: 'var(--s4)', marginTop: 'var(--s4)', flexWrap: 'wrap'}}>
                    <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><span style={{width: '7px', height: '7px', borderRadius: '2px', background: 'var(--lime)'}}></span><span style={{fontSize: '11.5px', color: 'var(--tx-muted)'}}>Home</span></span>
                    <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><span style={{width: '7px', height: '7px', borderRadius: '2px', background: 'var(--green)'}}></span><span style={{fontSize: '11.5px', color: 'var(--tx-muted)'}}>Near</span></span>
                    <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><span style={{width: '7px', height: '7px', borderRadius: '2px', background: 'var(--cyan)'}}></span><span style={{fontSize: '11.5px', color: 'var(--tx-muted)'}}>Europe</span></span>
                    <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><span style={{width: '7px', height: '7px', borderRadius: '2px', background: 'var(--violet)'}}></span><span style={{fontSize: '11.5px', color: 'var(--tx-muted)'}}>Distant</span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ MANAGER ═══ */}
      <section>
        <div className="wrap">
          <div className="rev">
            <div className="sec-label">The manager</div>
            <h2>You hired him. Now live with him.</h2>
            <p className="sec-intro">
              Your manager has a style, a temperament and opinions about the players you buy.
              You can influence him. You cannot overrule him.
            </p>
          </div>

          <div className="feat rev">
            <div>
              <h3>Alignment, not obedience</h3>
              <p>The blueprint says academy graduates must feature. He's playing the 29-year-old instead.
                Raise it once and it's a conversation. Raise it twice and it's a <b>formal warning</b>.
                The third time, you have a case to take to the board.</p>
              <p>Sacking him is a meeting, not a button. The board weighs results, cost, and how much
                they rate you. Ask at the wrong moment and you'll be the one who looks unreasonable.</p>
            </div>
            <div className="feat-vis">
              <div className="panel">
                <div className="panel-head">
                  <span className="panel-title">Derek Kaye</span>
                  <span className="badge b-cyan" style={{marginLeft: 'auto'}}>High Press · 4-3-3</span>
                </div>
                <div className="panel-body">
                  <div className="meter">
                    <div className="meter-h"><span className="meter-l">Relationship</span><span className="meter-v" style={{color: 'var(--green)'}}>71</span></div>
                    <div className="meter-t"><div className="meter-f" style={{width: '71%', background: 'var(--green)'}}></div></div>
                  </div>
                  <div className="meter">
                    <div className="meter-h"><span className="meter-l">Blueprint alignment</span><span className="meter-v" style={{color: 'var(--amber)'}}>68%</span></div>
                    <div className="meter-t"><div className="meter-f" style={{width: '68%', background: 'var(--amber)'}}></div></div>
                  </div>
                  <div className="meter">
                    <div className="meter-h"><span className="meter-l">Youth minutes</span><span className="meter-v" style={{color: 'var(--red)'}}>−53</span></div>
                    <div className="meter-t"><div className="meter-f" style={{width: '24%', background: 'var(--red)'}}></div></div>
                  </div>
                  <div className="quote">"The kid's not ready. I'm not throwing away points to prove a point."</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE TEAMS ═══ */}
      <ExploreTeams />

      {/* ═══ PROGRESS ═══ */}
      <section id="progress" style={{background: 'var(--bg-surface)', borderTop: '1px solid var(--line)'}}>
        <div className="narrow">
          <div className="rev">
            <div className="sec-label">Progress</div>
            <h2>Where it's up to.</h2>
            <p className="sec-intro">
              Groundwork is being built in the open by one person. The world is finished and
              populated. The game around it is next.
            </p>
          </div>

          <div className="road rev">
            <div className="road-item">
              <span className="road-state r-done">Built</span>
              <div className="road-body">
                <h4>The world</h4>
                <p>48 clubs across four divisions, 7,400 players, staff, finances, contracts.
                   Every player has attributes, potential and a development curve.</p>
              </div>
            </div>
            <div className="road-item">
              <span className="road-state r-done">Built</span>
              <div className="road-body">
                <h4>Match &amp; season engines</h4>
                <p>Matches simulate with commentary, live ratings and expected goals.
                   Full seasons run with promotion, relegation and playoffs.</p>
              </div>
            </div>
            <div className="road-item">
              <span className="road-state r-done">Built</span>
              <div className="road-body">
                <h4>Scouting</h4>
                <p>Three-tier fog of war, 27 regions worldwide, regional cost and accuracy.
                   Hidden gems that only reveal themselves if you look properly.</p>
              </div>
            </div>
            <div className="road-item">
              <span className="road-state r-now">In progress</span>
              <div className="road-body">
                <h4>The game itself</h4>
                <p>Day-by-day play, the inbox, transfers and negotiation, the club blueprint,
                   board and manager relationships.</p>
              </div>
            </div>
            <div className="road-item">
              <span className="road-state r-next">Next</span>
              <div className="road-body">
                <h4>Saves &amp; seasons</h4>
                <p>Long-running saves across many seasons, youth intakes, retirements,
                   and a world that keeps generating players as time passes.</p>
              </div>
            </div>
            <div className="road-item">
              <span className="road-state r-next">Later</span>
              <div className="road-body">
                <h4>Shared worlds</h4>
                <p>Up to five directors in one save, each at a different club,
                   competing for the same players.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="cta" id="join">
        <div className="wrap cta-in">
          <div className="sec-label">Waitlist</div>
          <h2>Be there for the first save.</h2>
          <p>
            No date yet, and no promises about one. Leave your email and you'll hear
            when there's something worth playing.
          </p>
          <div className="waitlist">
                  <WaitlistForm source="cta" />

      
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer>
        <div className="wrap foot-in">
          <a className="logo-lock" href="#top" aria-label="Groundwork">
            <LogoMark height={24} />
          </a>
          <span className="foot-copy">© 2026 Groundwork</span>
          <div className="foot-links">
            <a className="foot-link" href="#role">The role</a>
            <a className="foot-link" href="#world">The world</a>
            <a className="foot-link" href="#teams">The teams</a>
            <a className="foot-link" href="#progress">Progress</a>
            <Link className="foot-link" to="/login">Log in</Link>
            <Link className="foot-link" to="/privacy">Privacy</Link>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}
